import { useEffect, useState } from 'react';
import { ListTodo, Plus, X } from 'lucide-react';
import { practiceApi, errorDetailText } from '../services/api';
import { usePracticeStore } from '../store/practiceStore';
import { CaseListItem, Collaborator } from '../types';
import { TaskRow } from '../components/practice/TaskRow';
import { Button, EmptyState } from '../components/ui';

const INPUT_CLS =
  'w-full text-sm border border-legal-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-accent';

const TASK_TYPES: { value: string; label: string }[] = [
  { value: 'drafting', label: 'Drafting' },
  { value: 'research', label: 'Research' },
  { value: 'enquiry', label: 'Enquiry' },
  { value: 'argument_prep', label: 'Argument prep' },
  { value: 'document_verification', label: 'Document verification' },
  { value: 'filing', label: 'Filing' },
  { value: 'other', label: 'Other' },
];

/** Tasks: yours and the ones delegated to you, with a quick create form. */
export function TasksPage() {
  const { tasks, tasksScope, tasksFetchedAt, isFetchingTasks, fetchTasks } = usePracticeStore();
  const [showForm, setShowForm] = useState(false);
  const [showDone, setShowDone] = useState(false);

  // Create form state
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState('other');
  const [dueDate, setDueDate] = useState('');
  const [matterId, setMatterId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  // Case picker options — fetched locally (scope 'all') so the shared store's
  // Cases-page scope isn't disturbed.
  const [caseOptions, setCaseOptions] = useState<CaseListItem[] | null>(null);
  const [collabs, setCollabs] = useState<Collaborator[]>([]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!showForm || caseOptions !== null) return;
    practiceApi
      .listCases('all')
      .then(({ data }) => setCaseOptions(data))
      .catch(() => setCaseOptions([]));
  }, [showForm, caseOptions]);

  // Assignee options come from the picked case's collaborators.
  useEffect(() => {
    setAssigneeId('');
    if (!matterId) {
      setCollabs([]);
      return;
    }
    let stale = false;
    practiceApi
      .listCollaborators(matterId)
      .then(({ data }) => {
        if (!stale) setCollabs(data);
      })
      .catch(() => {
        if (!stale) setCollabs([]);
      });
    return () => {
      stale = true;
    };
  }, [matterId]);

  const activeCollabs = collabs.filter((c) => c.status === 'active' && c.collaborator_user_id);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      await practiceApi.createTask({
        title: title.trim(),
        task_type: taskType,
        due_date: dueDate || undefined,
        matter_id: matterId || undefined,
        assignee_user_id: assigneeId || undefined,
      });
      setTitle('');
      setDueDate('');
      setMatterId('');
      setAssigneeId('');
      setShowForm(false);
      fetchTasks();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not create the task.'));
    } finally {
      setIsSaving(false);
    }
  };

  const shown = tasks.filter((t) =>
    showDone ? true : t.status !== 'done' && t.status !== 'cancelled'
  );
  const isFirstLoad = tasks.length === 0 && (isFetchingTasks || tasksFetchedAt === null);

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-medium text-legal-900">Tasks</h1>
          <p className="text-legal-500 mt-1 text-[15px]">
            Work to do — yours and what you've delegated.
          </p>
        </div>
        <Button size="md" onClick={() => setShowForm((v) => !v)} className="shrink-0">
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          <span className="hidden sm:inline">{showForm ? 'Cancel' : 'New task'}</span>
          <span className="sm:hidden">{showForm ? 'Cancel' : 'New'}</span>
        </Button>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-white rounded-card border border-legal-200 p-4 mb-6 space-y-3 animate-slide-up"
        >
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={300}
            placeholder='e.g. "Draft reply notice for Meera Nair"'
            className={INPUT_CLS}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select value={taskType} onChange={(e) => setTaskType(e.target.value)} className={INPUT_CLS}>
              {TASK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              aria-label="Due date"
              className={INPUT_CLS}
            />
            <select
              value={matterId}
              onChange={(e) => setMatterId(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">No case</option>
              {(caseOptions ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              disabled={!matterId || activeCollabs.length === 0}
              className={`${INPUT_CLS} disabled:bg-legal-50 disabled:text-legal-400`}
            >
              <option value="">
                {!matterId
                  ? 'Assignee — pick a case first'
                  : activeCollabs.length === 0
                  ? "Invite a collaborator from the case's People tab"
                  : 'Assign to me'}
              </option>
              {activeCollabs.map((c) => (
                <option key={c.id} value={c.collaborator_user_id!}>
                  {c.collaborator_name || c.invited_email}
                </option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" size="md" isLoading={isSaving} disabled={!title.trim()}>
            Create task
          </Button>
        </form>
      )}

      {/* Scope tabs + done filter */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-1.5">
          {(
            [
              { value: 'mine', label: 'My tasks' },
              { value: 'assigned', label: 'Assigned to me' },
            ] as const
          ).map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => fetchTasks(s.value)}
              className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors press ${
                tasksScope === s.value
                  ? 'bg-accent text-legal-900'
                  : 'bg-white border border-legal-200 text-legal-600 hover:border-legal-300'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-legal-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
            className="h-4 w-4 rounded border-legal-300 text-accent focus:ring-accent"
          />
          Show done
        </label>
      </div>

      {isFirstLoad ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-legal-100 rounded-card" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={ListTodo}
          title={showDone ? 'No tasks' : 'All clear'}
          message={
            tasksScope === 'assigned'
              ? 'Nothing has been assigned to you.'
              : showDone
              ? 'No tasks yet — add one for yourself or a junior.'
              : 'No open tasks. Add one, or tick "Show done" to review finished work.'
          }
          action={
            tasksScope === 'mine' && (
              <Button size="md" onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4" /> New task
              </Button>
            )
          }
        />
      ) : (
        <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100 stagger-children">
          {shown.map((t) => (
            <TaskRow key={t.id} task={t} onChanged={() => fetchTasks()} />
          ))}
        </div>
      )}
    </div>
  );
}
